import React, { useCallback, useEffect, useMemo, useState } from "react"
import { compose, withHandlers } from "recompose"
import {
  withGoogleMap,
  withScriptjs,
  GoogleMap,
  Marker,
  InfoWindow,
} from "react-google-maps"
import { MarkerClusterer } from "react-google-maps/lib/components/addons/MarkerClusterer"
import MarkerClustererPlus from "marker-clusterer-plus"

// Pin grouping asks Google for the map's visible area every time it redraws,
// and the library crashes if Google has none to give: before the first draw,
// or after a map stops drawing (as a rejected API key does on local
// previews). Skip that redraw instead. The library redraws again the next
// time the map moves, by which point there is an area to measure.
const redrawGroups = MarkerClustererPlus.prototype.createClusters_
if (!redrawGroups.isGuarded) {
  const guardedRedraw = function guardedRedraw(...args) {
    const map = this.getMap()
    const bounds = map && map.getBounds()
    if (!bounds || !bounds.getNorthEast() || !bounds.getSouthWest()) {
      return undefined
    }
    return redrawGroups.apply(this, args)
  }
  guardedRedraw.isGuarded = true
  MarkerClustererPlus.prototype.createClusters_ = guardedRedraw
}

const DEFAULT_PIN = "/images/cortica-pin.png"
const DEFAULT_CLUSTER_COLOR = "#10389a"

// Clustered pins are drawn from inline SVG data URIs rather than the hosted
// PNGs MarkerClusterer reaches for by default, so the map never makes a request
// to a third party host.
const clusterCircle = (size, color) => {
  const svg = [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}">`,
    `<circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 3}"`,
    ` fill="${color}" stroke="#ffffff" stroke-width="3" />`,
    `</svg>`,
  ].join("")

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`
}

// Three sizes so a cluster of 30 reads as bigger than a cluster of 3.
const clusterStyles = color =>
  [36, 44, 54].map(size => ({
    height: size,
    textColor: "#ffffff",
    textSize: size >= 54 ? 15 : 13,
    url: clusterCircle(size, color),
    width: size,
  }))

const Map = compose(
  withHandlers(props => {
    const refs = {
      allCenter: undefined,
      allZoom: undefined,
      map: undefined,
    }
    const getBounds = () => {
      const bounds = new window.google.maps.LatLngBounds()

      if (props.markerPosition.length === 1) {
        const { lat, lon } = props.markerPosition[0]

        const expandLatBounds = 0.1
        const expandLonBounds = 0.1

        bounds.extend(
          new window.google.maps.LatLng(
            lat - expandLatBounds,
            lon - expandLonBounds
          )
        )
        bounds.extend(
          new window.google.maps.LatLng(
            lat + expandLatBounds,
            lon + expandLonBounds
          )
        )
      } else {
        props.markerPosition.forEach(marker => {
          bounds.extend(new window.google.maps.LatLng(marker.lat, marker.lon))
        })
      }

      return bounds
    }

    return {
      getZoomAndCenter: () => () => {
        if (!refs.allCenter && refs.map) {
          refs.allZoom = refs.map.getZoom()
          refs.allCenter = refs.map.getCenter()
        }
        return { allCenter: refs.allCenter, allZoom: refs.allZoom }
      },
      // Frames an area given by its edges, such as a whole state, with a
      // little room so pins on the edge are not cut off.
      frameArea: () => area => {
        if (!refs.map || !area) return
        refs.map.fitBounds(
          new window.google.maps.LatLngBounds(
            new window.google.maps.LatLng(area.south, area.west),
            new window.google.maps.LatLng(area.north, area.east)
          ),
          24
        )
      },
      // True once Google can say which part of the world the map is showing.
      // An empty answer comes back as corners with no position at all.
      hasVisibleArea: () => () => {
        const bounds = refs.map && refs.map.getBounds()
        return Boolean(bounds && bounds.getNorthEast() && bounds.getSouthWest())
      },
      // A page that has already chosen where to look (mapCenter) keeps that
      // view. Without this, a center set before the map finished loading was
      // thrown away by the frame-everything step below.
      //
      // fitPadding is optional. Google adds its own margin around the pins by
      // default, which on a wide spread of pins can push the map out a whole
      // zoom step. A page can pass 0 to fit the pins exactly instead.
      onMapMounted:
        ({ fitPadding, mapCenter }) =>
        ref => {
          if (!ref) return
          refs.map = ref
          if (mapCenter) return
          if (fitPadding === undefined) refs.map.fitBounds(getBounds())
          else refs.map.fitBounds(getBounds(), fitPadding)
        },
      onZoomChanged: () => () => {
        if (props.zoomChange) props.zoomChange(refs.map.getZoom())
      },
    }
  }),
  withScriptjs,
  withGoogleMap
)(
  ({
    clusterColor = DEFAULT_CLUSTER_COLOR,
    enableClustering,
    frameArea,
    frameTarget,
    hasVisibleArea,
    infoMode = "hover",
    isMarkerShown,
    mapCenter,
    mapOptions,
    mapZoom,
    markerIcon = DEFAULT_PIN,
    markerPosition,
    renderInfoContent,
    ...props
  }) => {
    // Hover mode keeps a single boolean, matching the original behavior used by
    // the clinic and Plano pages. Click mode tracks which pin is open so the
    // popup survives the mouse leaving the pin.
    const [showInfo, setShowInfo] = useState(false)
    const [openIndex, setOpenIndex] = useState(null)
    const isClickMode = infoMode === "click"

    const groupStyles = useMemo(
      () => clusterStyles(clusterColor),
      [clusterColor]
    )

    // Optional. A page sets frameTarget to an area's edges to move the map
    // there, for example when a visitor picks a state. Each request is a new
    // object, so picking the same area again frames it again.
    useEffect(() => {
      if (frameTarget) frameArea(frameTarget)
    }, [frameArea, frameTarget])

    // Pin grouping measures the visible area of the map, and crashes if Google
    // has none to give. That happens before the first draw, and for good when
    // the map cannot be drawn at all (for example a rejected API key, where
    // Google still reports the draw as finished). So the groups only start
    // once a finished draw comes with a real visible area.
    const [hasDrawn, setHasDrawn] = useState(false)
    const markDrawn = useCallback(() => {
      if (hasVisibleArea()) setHasDrawn(true)
    }, [hasVisibleArea])

    const handleInfoWindow = (index, show) => {
      markerPosition[index].showInfo = show
      setShowInfo(show)
    }

    const handleClick = index => {
      if (isClickMode)
        setOpenIndex(current => (current === index ? null : index))
      if (props.handleMarkerClick) props.handleMarkerClick(index)
    }

    const { allCenter, allZoom } = props.getZoomAndCenter()

    const markers = markerPosition.map((item, index) => {
      const isOpen = isClickMode
        ? openIndex === index
        : item.showInfo && showInfo

      return (
        <Marker
          icon={markerIcon}
          key={index}
          onClick={() => handleClick(index)}
          onMouseOut={
            isClickMode ? undefined : () => handleInfoWindow(index, false)
          }
          onMouseOver={
            isClickMode ? undefined : () => handleInfoWindow(index, true)
          }
          position={{ lat: item.lat, lng: item.lon }}
        >
          {isOpen && (
            <InfoWindow onCloseClick={() => setOpenIndex(null)}>
              {renderInfoContent ? (
                renderInfoContent(item, index)
              ) : (
                <h4 className="infoWindow mb-0 ml-1">{item.info}</h4>
              )}
            </InfoWindow>
          )}
        </Marker>
      )
    })

    return (
      <GoogleMap
        center={mapCenter || allCenter}
        // Optional colors and controls, set once when the map loads. Pages
        // that pass nothing get Google's default look, as before.
        defaultOptions={mapOptions}
        onIdle={enableClustering ? markDrawn : undefined}
        onZoomChanged={props.onZoomChanged}
        ref={props.onMapMounted}
        zoom={mapZoom || allZoom}
      >
        {isMarkerShown &&
          (enableClustering
            ? // zoomOnClick is what makes a cluster break apart into its
              // individual pins when tapped.
              hasDrawn && (
                <MarkerClusterer
                  averageCenter
                  enableRetinaIcons
                  gridSize={48}
                  minimumClusterSize={2}
                  styles={groupStyles}
                  zoomOnClick
                >
                  {markers}
                </MarkerClusterer>
              )
            : markers)}
      </GoogleMap>
    )
  }
)

export default Map
